import React, { useEffect, useRef, useState } from 'react';
import { Provider } from 'react-redux';
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import * as Sentry from '@sentry/react';
import 'dayjs/locale/es';
import dayjs from 'dayjs';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import {
  setTargetUrl,
  expandFunctionalLinkShortUtm,
  setGoogleClickId,
  setReferralCode,
  setAppsflyerUid,
  getApiEntrypoint,
  setFirebaseToken,
  bootstrapAuthFromUrl,
  syncSentryContext,
  sentryBeforeSend,
  removeStorageItem,
} from 'utils';
import { themeOptions } from './theme';
import { isDevelopment, isProduction } from './constants';
import { store } from './store/store';
import Router from 'routes/Router';
import { CookieConsentBanner } from 'components/CookieConsent';
import { ErrorBoundaryFallback } from 'components/ErrorBoundaryFallback';

dayjs.locale('es');

const fpPromise = FingerprintJS.load();

(async () => {
  const fp = await fpPromise;
  const result = await fp.get();
  window.fingerPrint = result;
})();

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

if (SENTRY_DSN) {
  const isProdEnv = process.env.REACT_APP_ENV === 'production';
  const isNotDev = process.env.REACT_APP_ENV !== 'development';
  const apiEntrypoint = getApiEntrypoint();

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_ENV,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllInputs: false,
        networkCaptureBodies: true,
        networkDetailAllowUrls: [window.location.origin, ...(apiEntrypoint ? [apiEntrypoint] : [])],
        networkRequestHeaders: ['Context-User-Uuid', 'Context-Session-Uuid', 'Context-Admin-Id'],
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: isProdEnv ? 0.2 : 1.0,
    replaysSessionSampleRate: isProdEnv ? 0.0 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    enabled: isNotDev,
    beforeSend: sentryBeforeSend,
  });
}

const theme = createTheme(themeOptions);

!isProduction && console.info('App linked to backend', getApiEntrypoint());

const App = () => {
  const [isBootstrapped, setIsBootstrapped] = useState(false);
  const isBootstrapStartedRef = useRef(false); // for skipping second init when strict mode is on

  useEffect(() => {
    if (isBootstrapStartedRef.current) {
      return;
    }

    isBootstrapStartedRef.current = true;
    let isMounted = true;

    const initializeApp = async () => {
      try {
        // Legacy cleanup: SET_PIN_CODE flag replaced by has_pin_code from personal-info API.
        removeStorageItem('set_pin_code');
        await bootstrapAuthFromUrl();
        syncSentryContext();
        setFirebaseToken();
        setReferralCode();
        setAppsflyerUid();
        setGoogleClickId();
        // Functional link: t/c → utm_template/utm_campaign до атрибуции (setTargetUrl).
        expandFunctionalLinkShortUtm();
        setTargetUrl();
      } catch (error: any) {
        console.error(error);
      } finally {
        if (isMounted) {
          setIsBootstrapped(true);
        }
      }
    };

    void initializeApp();

    return () => {
      isMounted = false;
    };
  }, []);

  if (!isBootstrapped) {
    return null;
  }

  const appTree = (
    <Sentry.ErrorBoundary fallback={<ErrorBoundaryFallback />}>
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router />
          <CookieConsentBanner />
        </ThemeProvider>
      </Provider>
    </Sentry.ErrorBoundary>
  );

  // NOTE: StrictMode run only in dev
  return isDevelopment ? <React.StrictMode>{appTree}</React.StrictMode> : appTree;
};

export default App;
